import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { diagnosticsApi, type DiagnosticTraceSummary, type DiagnosticTraceEntry, type TracedUser } from '@/services/api/agentApi';

interface DiagnosticsState {
  tracedUsers: TracedUser[];
  traces: DiagnosticTraceSummary[];
  total: number;
  selectedTrace: DiagnosticTraceSummary | null;
  selectedEntry: DiagnosticTraceEntry | null;
  filters: { userId?: string; orgId?: string };
  page: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
}

interface DiagnosticsActions {
  fetchStatus: () => Promise<void>;
  enableTracing: (userId: string, orgId: string, ttlHours: number) => Promise<void>;
  disableTracing: (userId: string, orgId: string) => Promise<void>;
  fetchTraces: () => Promise<void>;
  selectTrace: (trace: DiagnosticTraceSummary | null) => void;
  selectEntry: (entry: DiagnosticTraceEntry | null) => void;
  setFilter: (key: 'userId' | 'orgId', value: string) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
}

export const useDiagnosticsStore = create<DiagnosticsState & DiagnosticsActions>()(
  immer((set, get) => ({
    tracedUsers: [],
    traces: [],
    total: 0,
    selectedTrace: null,
    selectedEntry: null,
    filters: {},
    page: 0,
    pageSize: 50,
    isLoading: false,
    error: null,

    fetchStatus: async () => {
      try {
        const res = await diagnosticsApi.status();
        set((s) => { s.tracedUsers = res.data?.tracedUsers || []; });
      } catch (err) {
        set((s) => { s.error = (err as Error).message; });
      }
    },

    enableTracing: async (userId, orgId, ttlHours) => {
      try {
        await diagnosticsApi.enable({ userId, orgId, ttlHours });
        get().fetchStatus();
      } catch (err) {
        set((s) => { s.error = (err as Error).message; });
      }
    },

    disableTracing: async (userId, orgId) => {
      try {
        await diagnosticsApi.disable({ userId, orgId });
        get().fetchStatus();
      } catch (err) {
        set((s) => { s.error = (err as Error).message; });
      }
    },

    fetchTraces: async () => {
      set((s) => { s.isLoading = true; s.error = null; });
      try {
        const { filters, page, pageSize } = get();
        const params: Record<string, string | number> = { limit: pageSize, offset: page * pageSize };
        if (filters.userId) params.userId = filters.userId;
        if (filters.orgId) params.orgId = filters.orgId;
        const res = await diagnosticsApi.listTraces(params);
        set((s) => {
          s.traces = res.data?.traces || [];
          s.total = res.data?.total || 0;
          s.isLoading = false;
        });
      } catch (err) {
        set((s) => { s.isLoading = false; s.error = (err as Error).message; });
      }
    },

    selectTrace: (trace) => {
      set((s) => { s.selectedTrace = trace; s.selectedEntry = null; });
    },

    selectEntry: (entry) => {
      set((s) => { s.selectedEntry = entry; });
    },

    setFilter: (key, value) => {
      set((s) => {
        if (value) { s.filters[key] = value; }
        else { delete s.filters[key]; }
        s.page = 0;
      });
    },

    clearFilters: () => {
      set((s) => { s.filters = {}; s.page = 0; });
    },

    setPage: (page) => {
      set((s) => { s.page = page; });
    },
  })),
);

// Reset on logout to prevent data leakage between sessions
if (typeof window !== 'undefined') {
  import('../auth/auth-store').then(({ useAuthStore }) => {
    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, prev) => {
        if (prev && !isAuthenticated) {
          useDiagnosticsStore.setState({
            tracedUsers: [], traces: [], total: 0, selectedTrace: null,
            selectedEntry: null, filters: {}, page: 0, isLoading: false, error: null,
          });
        }
      }
    );
  });
}
